import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetProject } from "~/api/projects/projects";
import { LoaderWithSteps } from "~/components/LoaderWithSteps";
import { PageLayout } from "~/components/PageLayout";
import {
  DEMO_CASE_STEPS,
  SKY_ACCOUNT_CREATED,
  SKY_LAST_TRANSCRIBED_TEXT,
} from "~/constants/index";
import { UploadsBanner } from "~/features/case/components/list/UploadsBanner";
import { useDocumentsList } from "~/features/case/hooks/useDocumentsList";
import { generatePDFFile } from "~/features/chat/helpers/index";
import { ROUTES, useProjectParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";

enum DEMO_CASE_CREATION_STEP {
  GENERATE_PDF = 1,
  UPLOAD_FILE = 2,
  RUNNING_PIPELINE = 3,
}

const CASE_FILE_NAME = "case-1";

export const DemoCaseUploadPage = () => {
  useTrackPageVisit("app/demo-case-generation/visit");
  const navigate = useNavigate();
  const { organizationId, caseId } = useProjectParams();
  const { handleUpload, pendingUploads, activeUploads, hasWaitingDocuments } =
    useDocumentsList(organizationId, caseId);
  const [currentStep, setCurrentStep] = useState(
    DEMO_CASE_CREATION_STEP.GENERATE_PDF
  );
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasStartedUpload, setHasStartedUpload] = useState(false);
  const currentCase = useGetProject(organizationId, caseId).data;

  const isNewAccount = localStorage.getItem(SKY_ACCOUNT_CREATED) === "true";
  const transcribingText = localStorage.getItem(SKY_LAST_TRANSCRIBED_TEXT);

  useEffect(() => {
    if (
      !isNewAccount &&
      (!transcribingText || transcribingText?.length < 1) &&
      currentStep === DEMO_CASE_CREATION_STEP.GENERATE_PDF
    ) {
      navigate("/");
    }
  }, [transcribingText, isNewAccount, navigate]);

  const onFileUpload = useCallback(
    async (file: Blob) => {
      const mockFile = new File([file], `${CASE_FILE_NAME}.pdf`, {
        type: "application/pdf",
      });
      await handleUpload([mockFile]);
    },
    [handleUpload]
  );

  const onFileGeneration = useCallback(async () => {
    if (transcribingText && transcribingText?.length > 1) {
      const blob = await generatePDFFile(transcribingText, CASE_FILE_NAME);
      setCurrentStep(DEMO_CASE_CREATION_STEP.UPLOAD_FILE);
      if (blob) {
        setTimeout(() => {
          onFileUpload(blob);
        }, 1000);
      }
    }
  }, [transcribingText, onFileUpload]);

  const handleOpenCase = () => {
    navigate(ROUTES.CASE({ organizationId, caseId }));
  };

  const hasActiveUploads =
    pendingUploads > 0 || activeUploads > 0 || hasWaitingDocuments;

  useEffect(() => {
    if (
      currentStep === DEMO_CASE_CREATION_STEP.UPLOAD_FILE &&
      hasActiveUploads
    ) {
      setCurrentStep(DEMO_CASE_CREATION_STEP.RUNNING_PIPELINE);
    }
    if (
      currentStep === DEMO_CASE_CREATION_STEP.RUNNING_PIPELINE &&
      !hasActiveUploads
    ) {
      setIsCompleted(true);
      localStorage.removeItem(SKY_ACCOUNT_CREATED);
      localStorage.removeItem(SKY_LAST_TRANSCRIBED_TEXT);
    }
  }, [currentStep, hasActiveUploads]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasActiveUploads]);

  useEffect(() => {
    if (
      isNewAccount &&
      transcribingText &&
      transcribingText?.length > 1 &&
      currentCase &&
      !hasStartedUpload
    ) {
      switch (currentStep) {
        case DEMO_CASE_CREATION_STEP.GENERATE_PDF:
          setHasStartedUpload(true);
          setTimeout(() => {
            onFileGeneration();
          }, 1000);
          break;
        case DEMO_CASE_CREATION_STEP.UPLOAD_FILE:
          break;
        case DEMO_CASE_CREATION_STEP.RUNNING_PIPELINE:
          break;
      }
    }
  }, [
    currentStep,
    isNewAccount,
    onFileGeneration,
    transcribingText,
    currentCase,
    hasStartedUpload,
  ]);

  return (
    <PageLayout>
      <div className="w-full h-[85vh] flex flex-col items-center justify-center">
        {!isCompleted ? (
          <LoaderWithSteps
            steps={DEMO_CASE_STEPS}
            currentStep={currentStep === 3 ? 2 : currentStep}
          />
        ) : (
          <div className="flex flex-col items-center space-y-4">
            <h2 className="text-2xl font-semibold text-center">
              Case creation completed
            </h2>
            <button
              onClick={handleOpenCase}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Open Case
            </button>
          </div>
        )}
      </div>
      <UploadsBanner visible={hasActiveUploads} />
    </PageLayout>
  );
};
